import { useForm, zodResolver } from '@mantine/form'
import { useFocusWithin } from '@mantine/hooks'
import { z } from 'zod'

import { useGetWalletBalance } from '@repo/common/queries/blockchain/useGetWalletBalance'
import { useGetFundPrices } from '@repo/common/queries/funds'
import { useIsSuccess } from '@repo/common/queries/helpers/utilities'
import {
  type VaultSymbol,
  vaultToFundSymbol,
} from '@repo/common/queries/products'
import {
  useGetVaultDisabled,
  useGetVaultPricePerToken,
  useGetVaultWithdrawValues,
} from '@repo/common/queries/vaults'

const default_reason = 'Enter an amount'

// eslint-disable-next-line complexity
export const useVaultRedeemFields = ({ symbol }: { symbol: VaultSymbol }) => {
  const fund_symbol = vaultToFundSymbol(symbol)
  const form = useForm({
    mode: 'controlled',
    initialValues: {
      tokens_in: 1,
    },
    validateInputOnChange: true,
    clearInputErrorOnChange: false,
    validate: (values) => zodResolver(get_schema())(values),
  })
  const { get_isSuccess, combine_isSuccess } = useIsSuccess()

  const { balance: vault_balance, isConnected: wallet_isConnected } =
    combine_isSuccess(useGetWalletBalance(symbol))
  const { data: fund_prices } = combine_isSuccess(
    useGetFundPrices({ symbol: fund_symbol }),
  )
  const disabled_query = combine_isSuccess(
    useGetVaultDisabled({ symbol, type: 'mint' }),
  )

  const get_schema = () => {
    return z.object({
      tokens_in: z
        .number({ message: default_reason })
        .positive()
        .max(vault_balance, {
          message: `Not enough ${symbol} balance`,
        }),
    })
  }

  const tokens_in_focus = useFocusWithin()

  const { vault_price_per_token } = combine_isSuccess(
    useGetVaultPricePerToken({ symbol }),
  )

  const sell_usdc_value =
    form.getValues().tokens_in * (vault_price_per_token ?? 0)

  const { returned_fund_tokens, returned_usdc_tokens } = combine_isSuccess(
    useGetVaultWithdrawValues({
      symbol,
      redeem_vault_tokens: form.getValues().tokens_in,
    }),
  )

  const fund_usdc_value = returned_fund_tokens * (fund_prices?.dex ?? 0)

  const is_loading = get_isSuccess() == false

  const form_valid = form.isValid() && !is_loading

  const error_reason =
    disabled_query.reason ||
    get_schema().safeParse(form.getValues()).error?.errors[0]?.message ||
    default_reason

  return {
    form,
    fund_symbol,
    vault_balance,
    is_loading,
    wallet_isConnected,
    form_valid,
    paused: disabled_query.disabled,
    error_reason,
    tokens_in_focus,
    vault_price_per_token,
    fund_usdc_value,
    sell_usdc_value,
    returned_fund_tokens,
    returned_usdc_tokens,
  }
}
